import React, { useState, useMemo, useContext, useEffect, useCallback } from "react";
import AuthContext from "../Context/AuthProvider";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  
  Tooltip,
  IconButton,
  Fab,
  ListSubheader,
  CardHeader,
  Divider,
  useMediaQuery,
  useTheme,
  Box,
  CardActions,
  Grid2,
} from "@mui/material";
import dayjs from "dayjs";
import { paymentModes, refundPaymentModes } from "../Utility/Constants";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  getBillById,
  viewBillById,
} from "../API/BillingApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DataGrid } from "@mui/x-data-grid";
import { PaymentModes } from "../Utility/Constants";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import ServiceTableSection from "./ServiceTableSection";
import PaymentTableSection from "./PaymentTableSection";
import RefundTableSection from "./RefundTableSection";
import { numberToWords } from "../Utility/Utility";
import { formatAge } from "../Utility/Utility";
import { settleBill } from "../API/BillingApi";

const NewBill = ({ newBillInfo = {}, billId }) => {
  const authContext = useContext(AuthContext);
  const { auth } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [patientId, setPatientId] = useState(null);
  const [visitId, setVisitId] = useState(null);
  const paymentOptions = Object.values(PaymentModes);
  const [isSaving, setIsSaving] = useState(false);
  const { showSnackbar } = useSnackbar();

  const [billAmountSummary, setBillAmountSummary] = useState({
    totalAmount: 0,
    paidAmount: 0,
    refundedAmount: 0,
    balanceAmount: 0,
  });

  // New states to hold total amounts from respective sections
  const [paymentSectionTotalPaid, setPaymentSectionTotalPaid] = useState(0);
  const [refundSectionTotalRefunded, setRefundSectionTotalRefunded] = useState(0);
  const [serviceSectionTotalBilled, setServiceSectionTotalBilled] = useState(0);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState({ table: "", id: null });
  const [patientInfo, setPatientInfo] = useState(null);
  const [billDetails, setBillDetails] = useState(null);
  const formattedDate = billDetails?.createdOn
    ? dayjs(billDetails.createdOn).format("DD MMM YYYY hh:mm A").toUpperCase()
    : newBillInfo?.createdOn
    ? dayjs(newBillInfo.createdOn).format("DD MMM YYYY hh:mm A").toUpperCase()
    : "N/A";

  const loadBillingData = useCallback(async () => {
    // Ensure you handle the valid billId or newBillInfo.id
    const currentBillId = billId || newBillInfo?.id;

    if (!currentBillId) {
      setLoading(false);
      console.warn("No valid Bill ID to fetch billing data.");
      return;
    }

    setLoading(true);
    try {
      const result = await getBillById(auth, currentBillId);

      if (result.isSuccess) {
        const data = result.data;
        setPatientId(data.visit.patientId || null);
        setVisitId(data.visit?.id || null);
        setBillDetails({
          billNo: data.displayId || "N/A",
          createdBy: data.createdByUserName || "N/A",
          createdOn: data.createdOn || "N/A",
          billId: data.id || "N/A",
        });

        setPatientInfo({
          displayId: data.visit?.displayId || "N/A",
          patientName: `${data.visit?.patientFirstName || "N/A"} ${data.visit?.patientLastName || ""}`,
          phone: data.visit?.patientMobileNo || "N/A",
          age: data.visit?.patientAgeInYears,
          gender: data.visit?.patientGender || "N/A",
          address: data.visit?.patient?.address || "N/A",
          patientId: data.visit?.patientId || null,
        });

        const updatedSummary = {
          totalAmount: data.totalAmount ?? 0,
          paidAmount: data.paidAmount ?? 0,
          refundedAmount: data.totalRefundedAmount ?? 0,
          balanceAmount: data.balanceAmount ?? 0,
        };
        setBillAmountSummary(updatedSummary);

        // Set initial values for the section-specific states
        setServiceSectionTotalBilled(data.totalAmount ?? 0);
        setPaymentSectionTotalPaid(data.paidAmount ?? 0);
        setRefundSectionTotalRefunded(data.refundedAmount ?? 0);
      } else {
        setError("Failed to load billing details");
        console.error("API call failed:", result.data);
      }
    } catch (error) {
      console.error("Error fetching billing details", error);
      setError("Error fetching billing details");
    } finally {
      setLoading(false);
    }
  }, [auth, billId, newBillInfo.id]);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  const handleDeleteRow = (id, table) => {
    setRowToDelete({ id, table });
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    setOpenDeleteDialog(false);
    loadBillingData();
  };

  const handleSettleBill = async () => {
    if (!newBillInfo?.id && !billId) {
      showSnackbar("Missing bill ID. Cannot settle the bill.", "error");
      return;
    }

    const currentBillId = newBillInfo?.id || billId;
    setIsSaving(true);

    const response = await settleBill(auth, currentBillId);

    if (response.isSuccess) {
      showSnackbar("Bill settled successfully!", "success");
      await loadBillingData();
    } else {
      showSnackbar(response.data || "❌ Failed to settle the bill.", "error");
    }

    setIsSaving(false);
  };

  const handlePrintInvoice = async () => {
    showSnackbar("Generating invoice for printing...", "info");
    try {
      await viewBillById(auth, billId || newBillInfo.id);
    } catch (error) {
      showSnackbar("Failed to generate invoice.", "error");
      console.error("Print Invoice Error:", error);
    }
  };

  // Use the new section-specific states for display and balance calculation
  const totalAmount = serviceSectionTotalBilled.toFixed(2);
  const totalPaidAmount = paymentSectionTotalPaid.toFixed(2);
  const totalRefundAmount = refundSectionTotalRefunded.toFixed(2);
  const totalBalance = (serviceSectionTotalBilled - paymentSectionTotalPaid + refundSectionTotalRefunded).toFixed(2);

  return (
   <Grid container spacing={isMobile ? 1 : 2} sx={{ marginX: "0", marginY: "0", width: '100%' }}>
      {/* Action Buttons */}
      <Grid item xs={12} container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 1 : 2, justifyContent: isMobile ? 'flex-start' : 'flex-start' }}>
        <Grid item>
          <Tooltip title="Settle Bill" arrow>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSettleBill}
              size={isMobile ? "small" : "medium"}
            >
              Settle Bill
            </Button>
          </Tooltip>
        </Grid>
        <Grid item>
          <Tooltip title="Print Invoice" arrow>
            <Button
              variant="contained"
              color="secondary"
              onClick={handlePrintInvoice}
              size={isMobile ? "small" : "medium"}
            >
              Print
            </Button>
          </Tooltip>
        </Grid>
      </Grid>
<Grid2 container size={12} spacing={2} sx={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
      {/* Patient Information & Bill Information Cards */}
      <Grid item xs={12} container spacing={isMobile ? 1 : 2} sx={{ justifyContent: isMobile ? 'flex-start' : 'space-between', maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
        {/* Patient Info Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Card variant="outlined" sx={{ width: "100%", padding: "0px" }}>
            <CardContent sx={{ border: "1px" }}>
              <Typography variant="h6" align="left">
                Patient Information
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography align="left" variant="body2">
                <Box component="span" fontWeight="bold">Patient Name:</Box>{" "}
                {patientInfo?.patientName || `${newBillInfo?.patientFirstName || ""} ${newBillInfo?.patientLastName || ""}`}
              </Typography>
              <Typography align="left" variant="body2">
                <Box component="span" fontWeight="bold">Gender:</Box> {patientInfo?.gender || newBillInfo.gender || "N/A"}
              </Typography>
              <Typography align="left" variant="body2">
                <Box component="span" fontWeight="bold">Age:</Box> {formatAge(patientInfo?.age || newBillInfo?.age)}
              </Typography>
              <Typography align="left" variant="body2">
                <Box component="span" fontWeight="bold">Visit No:</Box>{" "}
                {patientInfo?.displayId || newBillInfo.visitDisplayId || "N/A"}
              </Typography>
              <Typography align="left" variant="body2">
                <Box component="span" fontWeight="bold">Phone No:</Box>{" "}
                {patientInfo?.phone || newBillInfo.patientMobileNumber || "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Bill Information Card */}
        <Grid item xs={12} md={6} lg={4} sx={{ ml: { xs: 0, sm: 'auto' } }}>
          <Card variant="outlined" sx={{ width: "100%" }}>
            <CardContent>
              <Typography variant="h6" align={isMobile ? "left" : "right"}>
                Bill Information
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography align={isMobile ? "left" : "right"} variant="body2">
                <Box component="span" fontWeight="bold">Bill No:</Box>{" "}
                {billDetails?.billNo || newBillInfo.displayId || "N/A"}
              </Typography>
              <Typography align={isMobile ? "left" : "right"} variant="body2">
                <Box component="span" fontWeight="bold">Bill Date:</Box> {formattedDate}
              </Typography>
              <Typography align={isMobile ? "left" : "right"} variant="body2">
                <Box component="span" fontWeight="bold">Created By:</Box>{" "}
                {billDetails?.createdBy || newBillInfo.createdByUserName || "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Amount Summary Cards */}
      <Grid item xs={12} container spacing={isMobile ? 1 : 2} sx={{ mt: isMobile ? 1 : 2, maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ width: "100%" }}>
            <CardHeader
              subheader="Total Billed Amount"
              subheaderTypographyProps={{ align: "center", variant: "body2", fontWeight: 'bold' }}
              sx={{ paddingY: "10px", textAlign: "center" }}
            ></CardHeader>
            <CardContent>
              <Typography variant="h4" align="center" >
                Rs {totalAmount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ width: "100%" }}>
            <CardHeader
              subheader="Total Paid Amount"
              subheaderTypographyProps={{ align: "center", variant: "body2", fontWeight: 'bold' }}
              sx={{ paddingY: "10px", textAlign: "center" }}
            ></CardHeader>
            <CardContent>
              <Typography variant="h4" align="center" >
                Rs {totalPaidAmount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ width: "100%" }}>
            <CardHeader
              subheader="Total Refunded Amount"
              subheaderTypographyProps={{ align: "center", variant: "body2", fontWeight: 'bold' }}
              sx={{ paddingY: "10px", textAlign: "center" }}
            ></CardHeader>
            <CardContent>
              <Typography variant="h4" align="center" >
                Rs {totalRefundAmount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ width: "100%" }}>
            <CardHeader
              subheader="Total Balance"
              subheaderTypographyProps={{ align: "center", variant: "body2", fontWeight: 'bold' }}
              sx={{ paddingY: "10px", textAlign: "center" }}
            ></CardHeader>
            <CardContent>
              <Typography
                variant="h4"
                align="center"
              >
                Rs {totalBalance}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Services Section */}
     <Grid item xs={12} sx={{ mt: isMobile ? 1 : 2, maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
        <ServiceTableSection
          billId={newBillInfo.id || billId}
          onServiceDataChange={loadBillingData}
          isSmallTablet={isSmallTablet}
          visitId={visitId}
          patientId={patientId}
          onTotalBilledChange={setServiceSectionTotalBilled} // Pass the setter function here
        />
      </Grid>

      {/* Payments Section */}
      <Grid item xs={12} sx={{ mt: isMobile ? 1 : 2, maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
        <PaymentTableSection
          billId={newBillInfo.id || billId}
          showSnackbar={showSnackbar}
          patientId={patientId}
          onPaymentDataChange={loadBillingData}
          onTotalPaidChange={setPaymentSectionTotalPaid}
          isSmallTablet={isSmallTablet}
        />
      </Grid>

      {/* Refunds Section */}
      <Grid item xs={12} sx={{ mt: isMobile ? 1 : 2, maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
        <RefundTableSection
            billId={newBillInfo.id || billId}
            showSnackbar={showSnackbar}
            onRefundDataChange={loadBillingData}
            onTotalRefundChange={setRefundSectionTotalRefunded} // Pass the setter function here
            isSmallTablet={isSmallTablet}
        />
      </Grid>
      </Grid2>
    </Grid>
  );
};

export default NewBill;